import React, { useCallback, useState } from "react";
import VinSearchBar from "../components/common/VinSearchBar.jsx";
import CarDetail from "../components/car/CarDetail.jsx";
import SalesHistoryBlock from "../components/car/SalesHistoryBlock.jsx";
import CarCalculator from "../components/car/CarCalculator.jsx";
import { carsApi } from "../api/auctiongate.js";

/**
 * Car Details tab. Variant 2 of the plugin integration:
 *   • AuctionGate seller / reserve / buy-now rows are folded into the
 *     "Auction" spec-block (CarDetail handles that via `agMeta` prop).
 *   • Sales History becomes a full-width block under the spec grid,
 *     styled exactly like the other spec blocks.
 *   • Carfax lives as a compact card in the sticky right column,
 *     injected via `rightColumnSlot`.
 *   • Calculator is a full-width spec-block at the bottom.
 */
export default function CarPage() {
  const [query, setQuery] = useState(null);
  const [auction, setAuction] = useState("copart");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const onSubmit = useCallback(async ({ query: q, auction: a }) => {
    setQuery(q);
    setAuction(a);
    setLoading(true);
    setError(null);
    try {
      setResult(await carsApi.get(q));
    } catch (e) {
      setError(e?.message || "Lookup failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const bidcars = result?.bidcars;
  const ag = result?.auctiongate?.history;
  const agMeta = result?.auctiongate;
  const auctionKey = result?.auctiongate?.auctionKey || auction || "copart";
  const queryLotNumber = result?.auctiongate?.lotNumber || bidcars?.lotNumber || null;
  const vin = bidcars?.vin || ag?.vin || "";
  const hasBidCars = bidcars && typeof bidcars === "object" && Object.keys(bidcars).length > 0;
  const hasAg = ag && (ag.vin || ag.historyAll);
  const hasAny = hasBidCars || hasAg;

  return (
    <>
      <VinSearchBar
        onSubmit={onSubmit}
        placeholder="Enter VIN, Lot number, or BidCars id…"
      />

      <div className="container stack-lg">
        {!query && (
          <div className="placeholder-panel">
            Enter a VIN or Lot number above to pull the full vehicle detail view,
            AHistory sale history, Carfax and the delivery calculator.
          </div>
        )}

        {query && !loading && error && (
          <div className="card login-error">{error}</div>
        )}

        {query && !loading && !error && !hasAny && (
          <div className="placeholder-panel">
            Nothing found for <b>{query}</b>. Start the BidCars backend on{" "}
            <code>:4000</code> so a VIN can be resolved to a lot number, or search
            by a Copart / IAAI lot number directly.
          </div>
        )}

        {query && (loading || hasAny) && !error && (
          <CarDetail
            bidcars={hasBidCars ? bidcars : null}
            agMeta={agMeta ?? null}
            loading={loading}
          />
        )}

        {query && (loading || hasAny) && !error && (
          <SalesHistoryBlock
            bidcars={bidcars ?? null}
            ag={ag}
            agMeta={agMeta}
            queryLotNumber={queryLotNumber}
            auctionKey={auctionKey}
          />
        )}

        {query && (loading || hasAny) && !error && (
          <CarCalculator
            bidcars={bidcars ?? {}}
            auctionKey={auctionKey}
            titleFee={Number(agMeta?.docStatus?.price) || 0}
          />
        )}

        {result && (
          <details className="card">
            <summary className="muted small" style={{ cursor: "pointer" }}>
              Debug · raw /api/cars response
            </summary>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 12,
                marginTop: 10,
                maxHeight: 400,
                overflow: "auto",
                background: "var(--color-bg-card-elevated)",
                padding: 12,
                borderRadius: 8,
              }}
            >
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </>
  );
}
